import styled, { keyframes, css } from 'styled-components';
import getThemeByProps from '@userzoomlibraries/toolkit-theme-manager/get-theme-by-props';
import React, { useContext, useRef, useState, useEffect, useCallback, useImperativeHandle } from 'react';
import PropTypes from '@userzoomlibraries/toolkit-js-utils/prop-types';
import FloatingElement from '@userzoomlibraries/toolkit-floating-element';
import noop from '@userzoomlibraries/toolkit-js-utils/noop';
import isString from '@userzoomlibraries/toolkit-js-utils/is-string';
import getId from '@userzoomlibraries/toolkit-js-utils/get-id';
import getSafeDocumentBody from '@userzoomlibraries/toolkit-js-utils/get-safe-document-body';
import getSafeHTMLElement from '@userzoomlibraries/toolkit-js-utils/get-safe-html-element';
import getFocusableElements from '@userzoomlibraries/toolkit-js-utils/get-focusable-elements';
import useEscape from '@userzoomlibraries/toolkit-react-utils/use-escape';
import useOnClickOutside from '@userzoomlibraries/toolkit-react-utils/use-on-click-outside';
import useDisableBackgroundScroll from '@userzoomlibraries/toolkit-react-utils/use-disable-background-scroll';
import { P as PLACEMENTS } from './index.js';
import { B as Backdrop } from './index.styled2.js';
import FocusJail from '@userzoomlibraries/toolkit-focus-jail';
import { jsx } from 'react/jsx-runtime';
import Button from '@userzoomlibraries/toolkit-button';
import Icon from '@userzoomlibraries/toolkit-icon';
import useAriaAttributes from '@userzoomlibraries/toolkit-react-utils/use-aria-attributes';
import useDataAttributes from '@userzoomlibraries/toolkit-react-utils/use-data-attributes';
import { f as fallbackCloseIcon } from './index2.js';

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var ANIMATIONS = Object.freeze({
  FADE: 'fade',
  NONE: 'none',
  SHIFT_AWAY: 'shift_away',
  SCALE: 'scale',
  PERSPECTIVE: 'perspective'
});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var DEFAULT_ANIMATION_DURATION = 300;

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getLastDialogOpened = function getLastDialogOpened() {
  var elementToCheck = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getSafeDocumentBody();
  var dialogOpened = elementToCheck.querySelector('[data-is-dialog="true"]');

  if (dialogOpened) {
    var subDialog = dialogOpened.querySelector('[data-is-dialog="true"]');

    if (subDialog) {
      return getLastDialogOpened(subDialog);
    }
  }

  return dialogOpened || elementToCheck;
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var DialogContext = /*#__PURE__*/React.createContext({});

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var useDialogContext = function useDialogContext() {
  return useContext(DialogContext);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var FocusTrap = function FocusTrap(_ref) {
  var className = _ref.className,
      children = _ref.children,
      animationType = _ref.animationType,
      placement = _ref.placement;

  var _useDialogInfo = useDialogContext(),
      dialogInfo = _useDialogInfo.dialogInfo;

  return /*#__PURE__*/jsx(FocusJail, {
    "aria-modal": "true",
    role: "dialog",
    tabIndex: "-1",
    "aria-labelledby": "".concat(dialogInfo.dialogId, "-header"),
    "aria-describedby": "".concat(dialogInfo.dialogId, "-body"),
    "data-placement": placement,
    className: "gz-dialog__content gz-dialog--animation-".concat(animationType, " ").concat(className),
    focusOnMount: dialogInfo.focusOnMount,
    initialFocusElement: dialogInfo.initialFocusElement,
    children: children
  });
};

FocusTrap.COMPONENT_ID = 'Gearz.Dialog.Content.FocusTrap';
FocusTrap.displayName = FocusTrap.COMPONENT_ID;
FocusTrap.propTypes = {
  animationType: PropTypes.oneOfWithDescription(['enter', 'leave'], 'Type of the animation.'),
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.nodeWithDescription('Content of the focus trap.'),
  placement: PropTypes.oneOfWithDescription(Object.values(PLACEMENTS), 'Placement of the dialog.')
};
FocusTrap.defaultProps = {
  animationType: 'leave',
  className: '',
  children: null,
  placement: PLACEMENTS.TOP
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var fadeInAnimation = keyframes(["0%{opacity:0;}100%{opacity:1;}"]);
var fadeOutAnimation = keyframes(["0%{opacity:1;}100%{opacity:0;}"]);

var getFadeInAnimation = function getFadeInAnimation() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref$duration;

  return css(["animation:", "ms ", ";"], duration, fadeInAnimation);
};

var getFadeOutAnimation = function getFadeOutAnimation() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$duration = _ref2.duration,
      duration = _ref2$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref2$duration;

  return css(["animation:", "ms ", ";"], duration, fadeOutAnimation);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var noneInAnimation = keyframes(["0%{opacity:0;}100%{opacity:1;}"]);
var noneOutAnimation = keyframes(["0%{opacity:1;}100%{opacity:0;}"]);

var getNoneInAnimation = function getNoneInAnimation() {
  return css(["animation:0s ", ";"], noneInAnimation);
};

var getNoneOutAnimation = function getNoneOutAnimation() {
  return css(["animation:0s ", ";"], noneOutAnimation);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var shiftAwayTopInAnimation = keyframes(["0%{opacity:0;transform:translateY(-20%);}100%{opacity:1;transform:translateY(0%);}"]);
var shiftAwayTopOutAnimation = keyframes(["0%{opacity:1;transform:translateY(0%);}100%{opacity:0;transform:translateY(-20%);}"]);
var shiftAwayRightInAnimation = keyframes(["0%{opacity:0;transform:translateX(20%);}100%{opacity:1;transform:translateX(0%);}"]);
var shiftAwayRightOutAnimation = keyframes(["0%{opacity:1;transform:translateX(0%);}100%{opacity:0;transform:translateX(20%);}"]);
var shiftAwayBottomInAnimation = keyframes(["0%{opacity:0;transform:translateY(20%);}100%{opacity:1;transform:translateY(0%);}"]);
var shiftAwayBottomOutAnimation = keyframes(["0%{opacity:1;transform:translateY(0%);}100%{opacity:0;transform:translateY(20%);}"]);
var shiftAwayLeftInAnimation = keyframes(["0%{opacity:0;transform:translateX(-20%);}100%{opacity:1;transform:translateX(0%);}"]);
var shiftAwayLeftOutAnimation = keyframes(["0%{opacity:1;transform:translateX(0%);}100%{opacity:0;transform:translateX(-20%);}"]);
var shiftAwayCenterInAnimation = keyframes(["0%{opacity:0;transform:translateY(-20%);}100%{opacity:1;transform:translateY(0%);}"]);
var shiftAwayCenterOutAnimation = keyframes(["0%{opacity:1;transform:translateY(0%);}100%{opacity:0;transform:translateY(20%);}"]);

var getShiftAwayInAnimation = function getShiftAwayInAnimation() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref$duration;

  return css(["&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"]{animation:", "ms ", ";}"], PLACEMENTS.TOP, PLACEMENTS.TOP_LEFT, PLACEMENTS.TOP_RIGHT, duration, shiftAwayTopInAnimation, PLACEMENTS.RIGHT, PLACEMENTS.RIGHT_BOTTOM, PLACEMENTS.RIGHT_TOP, duration, shiftAwayRightInAnimation, PLACEMENTS.BOTTOM, PLACEMENTS.BOTTOM_LEFT, PLACEMENTS.BOTTOM_RIGHT, duration, shiftAwayBottomInAnimation, PLACEMENTS.LEFT, PLACEMENTS.LEFT_BOTTOM, PLACEMENTS.LEFT_TOP, duration, shiftAwayLeftInAnimation, PLACEMENTS.CENTER, duration, shiftAwayCenterInAnimation);
};

var getShiftAwayOutAnimation = function getShiftAwayOutAnimation() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$duration = _ref2.duration,
      duration = _ref2$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref2$duration;

  return css(["&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{animation:", "ms ", ";}&[data-placement=\"", "\"]{animation:", "ms ", ";}"], PLACEMENTS.TOP, PLACEMENTS.TOP_LEFT, PLACEMENTS.TOP_RIGHT, duration, shiftAwayTopOutAnimation, PLACEMENTS.RIGHT, PLACEMENTS.RIGHT_BOTTOM, PLACEMENTS.RIGHT_TOP, duration, shiftAwayRightOutAnimation, PLACEMENTS.BOTTOM, PLACEMENTS.BOTTOM_LEFT, PLACEMENTS.BOTTOM_RIGHT, duration, shiftAwayBottomOutAnimation, PLACEMENTS.LEFT, PLACEMENTS.LEFT_BOTTOM, PLACEMENTS.LEFT_TOP, duration, shiftAwayLeftOutAnimation, PLACEMENTS.CENTER, duration, shiftAwayCenterOutAnimation);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var scaleInAnimation = keyframes(["0%{opacity:0;transform:scale3d(0.3,0.3,0.3);}100%{opacity:1;transform:scale3d(1,1,1);}"]);
var scaleOutAnimation = keyframes(["0%{opacity:1;transform:scale3d(1,1,1);}100%{opacity:0;transform:scale3d(0.3,0.3,0.3);}"]);

var getScaleInAnimation = function getScaleInAnimation() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref$duration;

  return css(["animation:", "ms ", ";"], duration, scaleInAnimation);
};

var getScaleOutAnimation = function getScaleOutAnimation() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$duration = _ref2.duration,
      duration = _ref2$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref2$duration;

  return css(["animation:", "ms ", ";"], duration, scaleOutAnimation);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var perspectiveInAnimation = keyframes(["0%{opacity:0;transform:perspective(300px) rotate3d(1,0,0,90deg);}70%{opacity:1;transform:perspective(300px) rotate3d(1,0,0,-15deg);}100%{opacity:1;transform:perspective(300px);}"]);
var perspectiveOutAnimation = keyframes(["0%{opacity:1;transform:perspective(300px);}30%{opacity:1;transform:perspective(300px) rotate3d(1,0,0,-15deg);}100%{opacity:0;transform:perspective(300px) rotate3d(1,0,0,90deg);}"]);

var getPerspectiveInAnimation = function getPerspectiveInAnimation() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref$duration;

  return css(["animation:", "ms ", ";"], duration, perspectiveInAnimation);
};

var getPerspectiveOutAnimation = function getPerspectiveOutAnimation() {
  var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref2$duration = _ref2.duration,
      duration = _ref2$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref2$duration;

  return css(["animation:", "ms ", ";"], duration, perspectiveOutAnimation);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var getAnimation = function getAnimation() {
  var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref$animation = _ref.animation,
      animation = _ref$animation === void 0 ? ANIMATIONS.FADE : _ref$animation,
      _ref$duration = _ref.duration,
      duration = _ref$duration === void 0 ? DEFAULT_ANIMATION_DURATION : _ref$duration;

  var animationInCSS;
  var animationOutCSS;
  var prerrequisitesCSS;

  switch (animation) {
    default:
    case ANIMATIONS.FADE:
      animationInCSS = getFadeInAnimation({
        duration: duration
      });
      animationOutCSS = getFadeOutAnimation({
        duration: duration
      });
      break;

    case ANIMATIONS.NONE:
      animationInCSS = getNoneInAnimation();
      animationOutCSS = getNoneOutAnimation();
      break;

    case ANIMATIONS.SHIFT_AWAY:
      prerrequisitesCSS = css(["&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{transform:translateX(0%);}&[data-placement=\"", "\"],&[data-placement=\"", "\"],&[data-placement=\"", "\"]{transform:translateY(0%);}"], PLACEMENTS.RIGHT, PLACEMENTS.RIGHT_BOTTOM, PLACEMENTS.RIGHT_TOP, PLACEMENTS.BOTTOM, PLACEMENTS.BOTTOM_LEFT, PLACEMENTS.BOTTOM_RIGHT);
      animationInCSS = getShiftAwayInAnimation({
        duration: duration
      });
      animationOutCSS = getShiftAwayOutAnimation({
        duration: duration
      });
      break;

    case ANIMATIONS.SCALE:
      animationInCSS = getScaleInAnimation({
        duration: duration
      });
      animationOutCSS = getScaleOutAnimation({
        duration: duration
      });
      break;

    case ANIMATIONS.PERSPECTIVE:
      animationInCSS = getPerspectiveInAnimation({
        duration: duration
      });
      animationOutCSS = getPerspectiveOutAnimation({
        duration: duration
      });
      break;
  }

  return css(["", " &.gz-dialog--animation-enter{", "}&.gz-dialog--animation-leave{", "}"], prerrequisitesCSS, animationInCSS, animationOutCSS);
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Content$2 = styled(FocusTrap).withConfig({
  componentId: "gz-dialog-1674727614741__sc-1mw7j2r-0"
})(["background-color:#fff;border-radius:6px;box-sizing:border-box;display:flex;flex-direction:column;margin:48px;max-height:calc(100% - 40px - 48px);max-width:calc(100% - 40px - 48px);outline:none;position:relative;white-space:normal;z-index:100;", ""], function (_ref) {
  var animation = _ref.animation,
      animationDuration = _ref.animationDuration,
      placement = _ref.placement;
  return getAnimation({
    animation: animation,
    duration: animationDuration,
    placement: placement
  });
});
Content$2.propTypes = FocusTrap.propTypes;
Content$2.defaultProps = FocusTrap.defaultProps;

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var Content$1 = function Content(_ref) {
  var className = _ref.className,
      children = _ref.children;

  var _useDialogInfo = useDialogContext(),
      dialogInfo = _useDialogInfo.dialogInfo;

  return /*#__PURE__*/jsx("div", {
    id: dialogInfo.dialogId,
    className: "gz-dialog__content-container ".concat(className),
    "data-placement": dialogInfo.placement,
    children: /*#__PURE__*/jsx(Content$2, {
      placement: dialogInfo.placement,
      animation: dialogInfo.animation,
      animationDuration: dialogInfo.animationDuration,
      animationType: dialogInfo.animationType,
      children: children
    })
  });
};

Content$1.COMPONENT_ID = 'Gearz.Dialog.Content';
Content$1.displayName = Content$1.COMPONENT_ID;
Content$1.propTypes = {
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.nodeWithDescription('Content of the dialog.')
};
Content$1.defaultProps = {
  className: '',
  children: null
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Content = styled(Content$1).withConfig({
  componentId: "gz-dialog-1674727614741__sc-1jeasyz-0"
})(["bottom:0;display:flex;left:0;outline:none;position:fixed;right:0;top:0;z-index:100;&[data-placement=", "]{align-items:flex-start;justify-content:center;}&[data-placement=", "]{align-items:flex-start;justify-content:flex-start;}&[data-placement=", "]{align-items:flex-start;justify-content:flex-end;}&[data-placement=", "]{align-items:center;justify-content:center;}&[data-placement=", "]{align-items:flex-end;justify-content:center;}&[data-placement=", "]{align-items:flex-end;justify-content:flex-start;}&[data-placement=", "]{align-items:flex-end;justify-content:flex-end;}&[data-placement=", "]{align-items:center;justify-content:flex-start;}&[data-placement=", "]{align-items:flex-start;justify-content:flex-start;}&[data-placement=", "]{align-items:flex-end;justify-content:flex-start;}&[data-placement=", "]{align-items:center;justify-content:flex-end;}&[data-placement=", "]{align-items:flex-start;justify-content:flex-end;}&[data-placement=", "]{align-items:flex-end;justify-content:flex-end;}"], PLACEMENTS.TOP, PLACEMENTS.TOP_LEFT, PLACEMENTS.TOP_RIGHT, PLACEMENTS.CENTER, PLACEMENTS.BOTTOM, PLACEMENTS.BOTTOM_LEFT, PLACEMENTS.BOTTOM_RIGHT, PLACEMENTS.LEFT, PLACEMENTS.LEFT_TOP, PLACEMENTS.LEFT_BOTTOM, PLACEMENTS.RIGHT, PLACEMENTS.RIGHT_TOP, PLACEMENTS.RIGHT_BOTTOM);
Content.propTypes = Content$1.propTypes;
Content.defaultProps = Content$1.defaultProps;

var _excluded = ["className", "onClick"];

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(Object(source), true).forEach(function (key) { _defineProperty$1(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var CloseButton$1 = function CloseButton(_ref) {
  var className = _ref.className,
      onClick = _ref.onClick,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var ariaAttributes = useAriaAttributes(otherProps);
  var dataAttributes = useDataAttributes(otherProps);

  var _useDialogInfo = useDialogContext(),
      dialogInfo = _useDialogInfo.dialogInfo;

  var onClickHandler = function onClickHandler(event) {
    dialogInfo.onClose();
    onClick(event);
  };

  return /*#__PURE__*/jsx(Button, _objectSpread$1(_objectSpread$1(_objectSpread$1({
    "aria-label": "Close",
    className: "gz-dialog__close-button ".concat(className),
    onClick: onClickHandler
  }, ariaAttributes), dataAttributes), {}, {
    children: /*#__PURE__*/jsx(Icon, {
      name: "close",
      fallback: fallbackCloseIcon
    })
  }));
};

CloseButton$1.COMPONENT_ID = 'Gearz.Dialog.CloseButton';
CloseButton$1.displayName = CloseButton$1.COMPONENT_ID;
CloseButton$1.propTypes = {
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  onClick: PropTypes.funcWithDescription('Callback called when click event is triggered.')
};
CloseButton$1.defaultProps = {
  className: '',
  onClick: noop
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var CloseButton = styled(CloseButton$1).withConfig({
  componentId: "gz-dialog-1674727614741__sc-1686akt-0"
})(["align-items:center;background-color:transparent;border:none;border-radius:6px;display:flex;height:32px;justify-content:center;padding:0;position:absolute;right:0.5rem;top:0.5rem;transition:all 250ms ease 0s;width:32px;&:focus{box-shadow:0 0 0 3px rgba(49,79,255,0.25);}&:hover{background-color:rgba(0,0,0,0.06);}&:active{background-color:rgba(0,0,0,0.08);}.gz-icon{height:12px;width:12px;}"]);
CloseButton.propTypes = CloseButton$1.propTypes;
CloseButton.defaultProps = CloseButton$1.defaultProps;

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var Header$1 = function Header(_ref) {
  var className = _ref.className,
      children = _ref.children;

  var _useDialogInfo = useDialogContext(),
      dialogInfo = _useDialogInfo.dialogInfo;

  return /*#__PURE__*/jsx("header", {
    id: "".concat(dialogInfo.dialogId, "-header"),
    className: "gz-dialog__header ".concat(className),
    children: children
  });
};

Header$1.COMPONENT_ID = 'Gearz.Dialog.Content.Header';
Header$1.displayName = Header$1.COMPONENT_ID;
Header$1.propTypes = {
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.nodeWithDescription('Content of the dialog\'s header.')
};
Header$1.defaultProps = {
  className: '',
  children: null
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Header = styled(Header$1).withConfig({
  componentId: "gz-dialog-1674727614741__sc-mm05lt-0"
})(["padding:12px;"]);
Header.propTypes = Header$1.propTypes;
Header.defaultProps = Header$1.defaultProps;

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var Body$1 = function Body(_ref) {
  var className = _ref.className,
      children = _ref.children;

  var _useDialogInfo = useDialogContext(),
      dialogInfo = _useDialogInfo.dialogInfo;

  return /*#__PURE__*/jsx("div", {
    id: "".concat(dialogInfo.dialogId, "-body"),
    className: "gz-dialog__body ".concat(className),
    children: children
  });
};

Body$1.COMPONENT_ID = 'Gearz.Dialog.Content.Body';
Body$1.displayName = Body$1.COMPONENT_ID;
Body$1.propTypes = {
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.nodeWithDescription('Content of the dialog\'s body.')
};
Body$1.defaultProps = {
  className: '',
  children: null
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Body = styled(Body$1).withConfig({
  componentId: "gz-dialog-1674727614741__sc-1drcn2w-0"
})(["overflow:auto;padding:12px;"]);
Body.propTypes = Body$1.propTypes;
Body.defaultProps = Body$1.defaultProps;

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */

var Footer$1 = function Footer(_ref) {
  var className = _ref.className,
      children = _ref.children;

  var _useDialogInfo = useDialogContext(),
      dialogInfo = _useDialogInfo.dialogInfo;

  return /*#__PURE__*/jsx("footer", {
    id: "".concat(dialogInfo.dialogId, "-footer"),
    className: "gz-dialog__footer ".concat(className),
    children: children
  });
};

Footer$1.COMPONENT_ID = 'Gearz.Dialog.Content.Footer';
Footer$1.displayName = Footer$1.COMPONENT_ID;
Footer$1.propTypes = {
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  children: PropTypes.nodeWithDescription('Content of the dialog\'s footer.')
};
Footer$1.defaultProps = {
  className: '',
  children: null
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Footer = styled(Footer$1).withConfig({
  componentId: "gz-dialog-1674727614741__sc-1d4v66v-0"
})(["padding:12px;"]);
Footer.propTypes = Footer$1.propTypes;
Footer.defaultProps = Footer$1.defaultProps;

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"]; if (_i == null) return; var _arr = []; var _n = true; var _d = false; var _s, _e; try { for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }
var Dialog$1 = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
      animation = _ref.animation,
      animationDuration = _ref.animationDuration,
      appendTo = _ref.appendTo,
      placement = _ref.placement,
      className = _ref.className,
      isOpened = _ref.isOpened,
      onOpened = _ref.onOpened,
      onClose = _ref.onClose,
      onClosed = _ref.onClosed,
      focusOnMount = _ref.focusOnMount,
      children = _ref.children,
      avoidCloseOnEscape = _ref.avoidCloseOnEscape,
      avoidCloseOnClickOutside = _ref.avoidCloseOnClickOutside,
      initialFocusElement = _ref.initialFocusElement,
      finalFocusElement = _ref.finalFocusElement;
  var dialogId = useRef(id || "gz-dialog-".concat(getId()));
  var dialogRef = useRef();
  var dialogContentRef = useRef();

  var _useState = useState({
    appendTo: appendTo,
    dataState: 'closed',
    dialogId: dialogId.current,
    shouldShow: false,
    isOpening: false,
    isFullOpened: false,
    isClosing: false,
    animation: animation,
    animationType: 'leave',
    animationDuration: animationDuration,
    placement: placement,
    onClose: onClose,
    focusOnMount: focusOnMount
  }),
      _useState2 = _slicedToArray(_useState, 2),
      dialogInfo = _useState2[0],
      setDialogInfo = _useState2[1];

  useEffect(function () {
    setDialogInfo(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        appendTo: appendTo
      });
    });
  }, [appendTo]);

  var startEnterAnimation = function startEnterAnimation() {
    var lastDialogOpened = getLastDialogOpened();
    var isFirstLevel = lastDialogOpened === getSafeDocumentBody();
    setDialogInfo(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        appendTo: isFirstLevel ? prevState.appendTo : lastDialogOpened || prevState.appendTo,
        shouldShow: true,
        isOpening: true,
        animationType: 'enter',
        dataState: 'opening'
      });
    });
  };

  var startLeaveAnimation = function startLeaveAnimation() {
    setDialogInfo(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        isClosing: true,
        isFullOpened: false,
        animationType: 'leave',
        dataState: 'closing'
      });
    });
  };

  var onAnimationEnd = function onAnimationEnd() {
    if (dialogInfo.animationType === 'leave') {
      var parentDialog;

      if (dialogRef.current) {
        parentDialog = dialogRef.current.parentNode.closest('[data-is-dialog="true"]');
      }

      if (dialogInfo.elementToFocusOnClose) {
        dialogInfo.elementToFocusOnClose.focus();
      } else if (parentDialog) {
        var _ref2 = getFocusableElements(parentDialog) || [],
            _ref3 = _slicedToArray(_ref2, 1),
            firstParentDialogFocusableElement = _ref3[0];

        if (firstParentDialogFocusableElement) {
          firstParentDialogFocusableElement.focus();
        }
      }

      setDialogInfo(function (prevState) {
        return _objectSpread(_objectSpread({}, prevState), {}, {
          isClosing: false,
          isFullOpened: false,
          shouldShow: false,
          dataState: 'closed'
        });
      });
      onClosed();
    } else if (dialogInfo.animationType === 'enter' && dialogInfo.isOpening) {
      var elementToFocusOnClose = isString(finalFocusElement) ? document.getElementById(finalFocusElement) : finalFocusElement;
      setDialogInfo(function (prevState) {
        return _objectSpread(_objectSpread({}, prevState), {}, {
          isOpening: false,
          isFullOpened: true,
          elementToFocusOnClose: elementToFocusOnClose,
          dataState: 'opened'
        });
      });
      onOpened();
    }
  };

  var onEscape = useCallback(function () {
    if (dialogRef.current) {
      var hasDialogChild = dialogRef.current.querySelector('[data-is-floating-element="true"]');

      if (!hasDialogChild && !dialogInfo.isClosing) {
        onClose();
      }
    }
  }, [onClose, dialogInfo.isClosing]);
  useEscape({
    onEscape: onEscape,
    preventClose: avoidCloseOnEscape
  });
  useOnClickOutside({
    elementRef: dialogContentRef,
    callback: onEscape,
    disabled: avoidCloseOnClickOutside
  });
  useDisableBackgroundScroll({
    enabled: dialogInfo.shouldShow
  });
  useEffect(function () {
    setDialogInfo(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), {}, {
        animation: animation,
        animationDuration: animationDuration,
        focusOnMount: focusOnMount,
        onClose: onEscape,
        placement: placement,
        initialFocusElement: initialFocusElement,
        finalFocusElement: finalFocusElement
      });
    });
  }, [animation, animationDuration, focusOnMount, onEscape, placement, initialFocusElement, finalFocusElement]);
  useEffect(function () {
    if (isOpened && !dialogInfo.shouldShow) {
      startEnterAnimation();
    }

    if (!isOpened && dialogInfo.shouldShow) {
      startLeaveAnimation();
    }
  }, [isOpened, dialogInfo.shouldShow]);
  useEffect(function () {
    if (dialogInfo.isFullOpened && dialogInfo.shouldShow && !dialogInfo.isClosing) {
      // Save reference of Dialog.Content to be able to close on click outside
      var dialogContentContainer = dialogRef.current.querySelector("#".concat(dialogInfo.dialogId));
      dialogContentRef.current = dialogContentContainer.querySelector('.gz-dialog__content');
      var parentDialog = dialogRef.current.closest('[data-is-floating-element="true"]');

      if (parentDialog && !parentDialog.isSameNode(dialogRef.current)) {
        var _getComputedStyle = getComputedStyle(parentDialog),
            parentZIndex = _getComputedStyle.zIndex;

        dialogRef.current.style['z-index'] = +parentZIndex + 1;
      }
    }
  }, [dialogInfo.shouldShow, dialogInfo.isOpening, dialogInfo.isClosing, dialogInfo.animation, dialogInfo.dialogId, dialogInfo.isFullOpened, onOpened, children]);
  useImperativeHandle(ref, function () {
    return dialogRef.current;
  });
  return dialogInfo.shouldShow && /*#__PURE__*/jsx(DialogContext.Provider, {
    value: {
      dialogInfo: dialogInfo,
      setDialogInfo: setDialogInfo
    },
    children: /*#__PURE__*/jsx(FloatingElement, {
      appendTo: dialogInfo.appendTo,
      "data-id": dialogId.current,
      ref: dialogRef,
      className: "gz-dialog gz-dialog--animation-".concat(dialogInfo.animationType, " ").concat(className),
      onAnimationEnd: onAnimationEnd,
      "data-is-dialog": "true",
      "data-state": dialogInfo.dataState,
      children: children
    })
  });
});
Dialog$1.COMPONENT_ID = 'Gearz.Dialog';
Dialog$1.displayName = Dialog$1.COMPONENT_ID;
Dialog$1.Backdrop = Backdrop;
Dialog$1.Content = Content;
Dialog$1.Header = Header;
Dialog$1.Body = Body;
Dialog$1.Footer = Footer;
Dialog$1.CloseButton = CloseButton;
Dialog$1.ANIMATIONS = ANIMATIONS;
Dialog$1.PLACEMENTS = PLACEMENTS;
Dialog$1.propTypes = {
  id: PropTypes.stringWithDescription('Id of the dialog.'),
  animation: PropTypes.oneOfWithDescription(Object.values(ANIMATIONS), 'Animation to use on open/close the dialog.'),
  animationDuration: PropTypes.numberWithDescription('Duration of the animation.'),
  appendTo: PropTypes.oneOfTypeWithDescription([PropTypes.string, PropTypes.instanceOf(getSafeHTMLElement())], 'Destination element to append the dialog.'),
  className: PropTypes.stringWithDescription('Custom class to be applied.'),
  placement: PropTypes.oneOfWithDescription(Object.values(PLACEMENTS), 'Placement of the dialog.'),
  isOpened: PropTypes.boolWithDescription('Flag to determine if the dialog is opened/closed.'),
  focusOnMount: PropTypes.boolWithDescription('Flag to focus the first focusable element of the dialog on mount.'),
  onOpened: PropTypes.funcWithDescription('Callback called when the opening animation ends.'),
  onClose: PropTypes.funcWithDescription('Callback called when the closing animation starts.'),
  onClosed: PropTypes.funcWithDescription('Callback called when the closing animation ends.'),
  children: PropTypes.nodeWithDescription('Content of the dialog.'),
  avoidCloseOnEscape: PropTypes.boolWithDescription('Flag to deactivate the close on escape key pressed.'),
  avoidCloseOnClickOutside: PropTypes.boolWithDescription('Flag to deactivate the close on click outside the dialog.'),
  initialFocusElement: PropTypes.oneOfTypeWithDescription([PropTypes.string, // id
  PropTypes.instanceOf(getSafeHTMLElement()) // DOM element
  ], 'Element to be focused on mount the dialog. Only if focusOnMount is true.'),
  finalFocusElement: PropTypes.oneOfTypeWithDescription([PropTypes.string, // id
  PropTypes.instanceOf(getSafeHTMLElement()) // DOM element
  ], 'Element to be focused when the dialog is closed.')
};
Dialog$1.defaultProps = {
  id: null,
  animation: ANIMATIONS.FADE,
  animationDuration: DEFAULT_ANIMATION_DURATION,
  appendTo: getSafeDocumentBody(),
  className: '',
  placement: PLACEMENTS.CENTER,
  isOpened: false,
  focusOnMount: true,
  onOpened: noop,
  onClose: noop,
  onClosed: noop,
  children: null,
  avoidCloseOnEscape: false,
  avoidCloseOnClickOutside: false,
  initialFocusElement: null,
  finalFocusElement: null
};

/**
 * @license
 * This source code is and remains the property of UserZoom Technologies Inc (sucursal en España).
 * Dissemination of this information or reproduction of this material is strictly
 * forbidden unless prior written permission is obtained from
 * UserZoom Technologies Inc (sucursal en España) (https://www.userzoom.com).
 */
var Dialog = styled(Dialog$1).withConfig({
  componentId: "gz-dialog-1674727614741__sc-lfo56f-0"
})(["bottom:0;left:0;position:fixed;right:0;top:0;z-index:100;&.gz-dialog--animation-enter{", "}&.gz-dialog--animation-leave{", "}", ""], function (_ref) {
  var animation = _ref.animation,
      animationDuration = _ref.animationDuration;
  return getFadeInAnimation({
    duration: animation !== ANIMATIONS.NONE ? animationDuration : 1
  });
}, function (_ref2) {
  var animation = _ref2.animation,
      animationDuration = _ref2.animationDuration;
  return getFadeOutAnimation({
    duration: animation !== ANIMATIONS.NONE ? animationDuration : 1
  });
}, function (props) {
  return getThemeByProps({
    componentId: Dialog$1.COMPONENT_ID,
    props: props
  });
});
Dialog.displayName = Dialog$1.displayName;
Dialog.propTypes = Dialog$1.propTypes;
Dialog.defaultProps = Dialog$1.defaultProps;

export { Body as B, Content as C, Dialog as D, Footer as F, Header as H, CloseButton as a };
